import type { IProject } from './types/index.js';

/**
 * The work-area selector's own order (F-0186, I-0560).
 *
 * ⛔ **This is the one definition of "the order work areas are listed in", and it is deliberately
 * neither alphabetical nor by any count.** It is the reader's own arrangement — their saved
 * `workAreaOrder`, with children under their parents — because a reader builds a spatial memory of
 * that list and a second ordering breaks it. Every surface that lists work areas reads this: the
 * selector, *Decisions for me* under All work areas, and `featit decisions --all-work-areas`.
 *
 * It lives in `@featit/shared` for that reason. It was `code/web/src/features/work-areas/` until
 * I-0560 needed the CLI to group by the same order; a second copy there would have been a second
 * answer to the same question, and the two would have drifted.
 */
/**
 * All the order needs of a work area: who it is, and whose child it is.
 *
 * ⭐ Structural on purpose. The web holds `IProject`; the CLI holds its own `ProjectRef`, which is a
 * different type carrying the same two facts. Requiring `IProject` here would have forced the CLI to
 * either convert or to sort for itself, and the second copy is the drift this module exists to
 * prevent.
 */
export interface IWorkAreaOrderNode {
  id: string;
  parentId?: string | null;
}

export interface WorkAreaHierarchyRow<TWorkArea extends IWorkAreaOrderNode = IProject> {
  project: TWorkArea;
  depth: number;
  hasChildren: boolean;
}

export function flattenWorkAreaHierarchy<TWorkArea extends IWorkAreaOrderNode>(
  projects: readonly TWorkArea[],
  workAreaOrder: string[] = [],
): WorkAreaHierarchyRow<TWorkArea>[] {
  const byId = new Map(projects.map((project) => [project.id, project]));
  const childrenByParent = new Map<string, TWorkArea[]>();
  const sourceIndex = new Map(projects.map((project, index) => [project.id, index]));
  const orderIndex = new Map(workAreaOrder.map((id, index) => [id, index]));
  const compareProjects = (left: TWorkArea, right: TWorkArea): number => {
    const leftOrder = orderIndex.get(left.id);
    const rightOrder = orderIndex.get(right.id);
    if (leftOrder !== undefined || rightOrder !== undefined) {
      if (leftOrder === undefined) return 1;
      if (rightOrder === undefined) return -1;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    }
    return (sourceIndex.get(left.id) ?? 0) - (sourceIndex.get(right.id) ?? 0);
  };

  projects.forEach((project) => {
    if (!project.parentId || project.parentId === project.id || !byId.has(project.parentId)) return;
    const siblings = childrenByParent.get(project.parentId) ?? [];
    siblings.push(project);
    childrenByParent.set(project.parentId, siblings);
  });
  childrenByParent.forEach((children) => children.sort(compareProjects));

  const rows: WorkAreaHierarchyRow<TWorkArea>[] = [];
  const emitted = new Set<string>();

  const visit = (project: TWorkArea, depth: number) => {
    if (emitted.has(project.id)) return;
    emitted.add(project.id);
    const children = childrenByParent.get(project.id) ?? [];
    rows.push({ project, depth, hasChildren: children.length > 0 });
    children.forEach((child) => visit(child, depth + 1));
  };

  projects
    .filter(
      (project) =>
        !project.parentId || project.parentId === project.id || !byId.has(project.parentId),
    )
    .sort(compareProjects)
    .forEach((project) => visit(project, 0));

  // Legacy cycles have no root. Emit each remaining component once instead of hiding it.
  projects.forEach((project) => visit(project, 0));

  return rows;
}

/** The selector's order as bare work areas — what a grouping needs and all it needs. */
export function orderedWorkAreas<TWorkArea extends IWorkAreaOrderNode>(
  projects: readonly TWorkArea[],
  workAreaOrder: string[] = [],
): TWorkArea[] {
  return flattenWorkAreaHierarchy(projects, workAreaOrder).map(({ project }) => project);
}

export function moveWorkAreaWithinParent<TWorkArea extends IWorkAreaOrderNode>(
  projects: readonly TWorkArea[],
  workAreaOrder: string[],
  activeId: string,
  overId: string,
): string[] | null {
  if (activeId === overId) return null;
  const byId = new Map(projects.map((project) => [project.id, project]));
  const active = byId.get(activeId);
  const over = byId.get(overId);
  if (!active || !over || normalizedParentId(active, byId) !== normalizedParentId(over, byId)) {
    return null;
  }

  const orderedIds = flattenWorkAreaHierarchy(projects, workAreaOrder).map(
    ({ project }) => project.id,
  );
  const siblings = orderedIds.filter(
    (id) => normalizedParentId(byId.get(id)!, byId) === normalizedParentId(active, byId),
  );
  const activeIndex = siblings.indexOf(activeId);
  const overIndex = siblings.indexOf(overId);
  if (activeIndex < 0 || overIndex < 0) return null;
  siblings.splice(overIndex, 0, siblings.splice(activeIndex, 1)[0]);

  let siblingIndex = 0;
  return orderedIds.map((id) =>
    normalizedParentId(byId.get(id)!, byId) === normalizedParentId(active, byId)
      ? siblings[siblingIndex++]
      : id,
  );
}

function normalizedParentId<TWorkArea extends IWorkAreaOrderNode>(
  project: TWorkArea,
  byId: Map<string, TWorkArea>,
): string | null {
  if (!project.parentId || project.parentId === project.id || !byId.has(project.parentId)) {
    return null;
  }
  return project.parentId;
}

/** All a namespace needs to take part in this module's ordering: an id, a parent, its kind and its handle. */
export interface IWorkAreaOrderNamespaceNode extends IWorkAreaOrderNode {
  /** `'user'` for the reader's own, personal namespace; anything else is an organisation or subgroup. */
  kind: string;
  /** The namespace's own handle, which orders the ones the reader has not arranged. */
  path: string;
}

/**
 * Every organisation and subgroup, depth-first, in the reader's saved `namespaceOrder`; the reader's
 * own personal namespace is not among them (it is always first and never arranged).
 *
 * ⭐ A namespace the reader has not arranged comes after the ones they have, by its handle among
 * its siblings — the order every namespace had before F-0380, and still has for a reader who has
 * arranged nothing. `flattenWorkAreaHierarchy` alone falls back to the order the namespaces arrived
 * in, which put a subgroup created a moment ago last under its organisation instead of where its
 * handle sorts (gate 438, `namespaces-sidebar.spec.ts`). One function, so the sidebar, every
 * aggregate surface and the CLI give the same answer.
 */
export function flattenNamespaceHierarchy<TNamespace extends IWorkAreaOrderNamespaceNode>(
  namespaces: readonly TNamespace[],
  namespaceOrder: string[] = [],
): WorkAreaHierarchyRow<TNamespace>[] {
  return flattenWorkAreaHierarchy(arrangeableNamespacesByHandle(namespaces), namespaceOrder);
}

/** The organisations and subgroups, by handle: the order they have before anyone arranges them. */
function arrangeableNamespacesByHandle<TNamespace extends IWorkAreaOrderNamespaceNode>(
  namespaces: readonly TNamespace[],
): TNamespace[] {
  return namespaces
    .filter((namespace) => namespace.kind !== 'user')
    .sort((left, right) => left.path.localeCompare(right.path));
}

/**
 * Every work area across every namespace, in the selector's own order (F-0380).
 *
 * ⭐ Namespace by namespace, in the reader's saved `namespaceOrder` — their own personal namespace
 * first and never reordered (there is only ever one), then every organisation and subgroup in the
 * arrangement they saved, siblings among siblings exactly as `flattenWorkAreaHierarchy` already
 * orders a work area among the others under its own parent. Within each namespace, its work areas
 * in the reader's saved `workAreaOrder` — unchanged from before this namespace layer existed.
 *
 * This is reused rather than reimplemented: the namespace layer is `flattenWorkAreaHierarchy` called
 * on the namespaces instead of the work areas, because a namespace's "position among its siblings"
 * is the same question a work area's is, and a second ordering function would have been a second
 * answer this module exists to prevent (see the module doc above).
 *
 * A work area in a namespace this read does not know about (not yet caught up, or the namespace
 * tree failed to load) is appended after every known namespace, rather than dropped or reshuffled
 * among them — the fallback position `groupWorkAreasByNamespace` (`code/web`) gives the same case.
 */
export function flattenWorkAreasAcrossNamespaces<
  TWorkArea extends IWorkAreaOrderNode & { namespaceId?: string | null },
  TNamespace extends IWorkAreaOrderNamespaceNode,
>(
  projects: readonly TWorkArea[],
  namespaces: readonly TNamespace[] | null,
  workAreaOrder: string[] = [],
  namespaceOrder: string[] = [],
): WorkAreaHierarchyRow<TWorkArea>[] {
  // `null` means the namespace tree has not answered (or could not be read) — this reads exactly
  // as every aggregate surface read before F-0380, a flat `workAreaOrder` over every work area,
  // namespace groupings aside. Without this, a work area that happens to carry its own
  // `namespaceId` (most of them do) would still get bucketed by namespace below, just with every
  // bucket "unknown" and ordered by nothing but which one happened to be seen first — a worse
  // approximation of the saved order than ignoring the namespace layer entirely until it answers.
  if (namespaces === null) return flattenWorkAreaHierarchy(projects, workAreaOrder);

  const NO_NAMESPACE = '\u0000no-namespace';
  const byNamespace = new Map<string, TWorkArea[]>();
  for (const project of projects) {
    const key = project.namespaceId ?? NO_NAMESPACE;
    const bucket = byNamespace.get(key);
    if (bucket) bucket.push(project);
    else byNamespace.set(key, [project]);
  }

  const known = namespaces;
  // The tree lists exactly one personal namespace: the reader's own. It is never a sibling of an
  // organisation, so it is kept out of the layer `flattenWorkAreaHierarchy` orders below.
  const personalId = known.find((namespace) => namespace.kind === 'user')?.id ?? null;
  const orderedNamespaceIds = [
    ...(personalId ? [personalId] : []),
    ...flattenNamespaceHierarchy(known, namespaceOrder).map((row) => row.project.id),
  ];

  const rows: WorkAreaHierarchyRow<TWorkArea>[] = [];
  const consumed = new Set<string>();
  for (const namespaceId of orderedNamespaceIds) {
    consumed.add(namespaceId);
    rows.push(...flattenWorkAreaHierarchy(byNamespace.get(namespaceId) ?? [], workAreaOrder));
  }
  for (const [key, members] of byNamespace) {
    if (consumed.has(key)) continue;
    rows.push(...flattenWorkAreaHierarchy(members, workAreaOrder));
  }
  return rows;
}

/** {@link flattenWorkAreasAcrossNamespaces} as bare work areas — what a flat list needs and all it needs. */
export function orderedWorkAreasAcrossNamespaces<
  TWorkArea extends IWorkAreaOrderNode & { namespaceId?: string | null },
  TNamespace extends IWorkAreaOrderNamespaceNode,
>(
  projects: readonly TWorkArea[],
  namespaces: readonly TNamespace[] | null,
  workAreaOrder: string[] = [],
  namespaceOrder: string[] = [],
): TWorkArea[] {
  return flattenWorkAreasAcrossNamespaces(projects, namespaces, workAreaOrder, namespaceOrder).map(
    ({ project }) => project,
  );
}

/**
 * Drag/keyboard reorder for a namespace among its siblings (F-0380): the same rule
 * {@link moveWorkAreaWithinParent} already applies to a work area, reused rather than reimplemented.
 * The reader's own personal namespace is filtered out of the candidate set first — there is only
 * ever one, so it is never a sibling and never a valid `activeId`/`overId` here.
 */
export function moveNamespaceWithinParent<TNamespace extends IWorkAreaOrderNamespaceNode>(
  namespaces: readonly TNamespace[],
  namespaceOrder: string[],
  activeId: string,
  overId: string,
): string[] | null {
  // From the order the reader sees (`flattenNamespaceHierarchy`), or a move would start from an
  // arrangement that is not the one on screen.
  return moveWorkAreaWithinParent(
    arrangeableNamespacesByHandle(namespaces),
    namespaceOrder,
    activeId,
    overId,
  );
}
